/**
 * Estymacja pozycji bramy na podstawie stanu, czasu rozpoczęcia ruchu i jego czasu trwania.
 * Pozycja: 0 = zamknięta, 1 = otwarta.
 */
export const DEFAULT_GATE_DURATION = 30000

export interface GateMotion {
  state: warehouse.GateState
  /** ms (Date.now) – kiedy zaczął się aktualny stan */
  since?: number
  /** pozycja 0..1 w chwili `since` */
  startPosition?: number
  /** ms – czas pełnego ruchu 0 → 1 dla aktualnego kierunku */
  duration?: number
  /** ISO8601 z backendu – używane, gdy brak `since` (np. dane spoza reduxa) */
  stateAt?: string | null
}

const clamp = (value: number) => Math.min(1, Math.max(0, value))

export const isGateMoving = (state?: warehouse.GateState) =>
  state === 'gate_opening' || state === 'gate_closing'

export const getGateTargetPosition = (gate: GateMotion, now = Date.now()) => {
  switch (gate.state) {
    case 'gate_opening':
    case 'gate_opened': return 1
    case 'gate_closing':
    case 'gate_closed': return 0
    default: return getGatePosition(gate, now)
  }
}

export const getGatePosition = (gate: GateMotion, now = Date.now()): number => {
  const { state, since, duration = DEFAULT_GATE_DURATION } = gate
  switch (state) {
    case 'gate_opened': return 1
    case 'gate_closed': return 0
    case 'gate_opening':
    case 'gate_closing': {
      const start = gate.startPosition ?? (state === 'gate_opening' ? 0 : 1)
      const elapsed = since ? Math.max(0, now - since) : 0
      const delta = duration > 0 ? elapsed / duration : 1
      return clamp(state === 'gate_opening' ? start + delta : start - delta)
    }
    default: return clamp(gate.startPosition ?? 0.5)
  }
}

/**
 * Zamienia `stateAt` (ISO8601) z backendu na ms. Nie pozwala na czas z przyszłości
 * (zegar telefonu może się spóźniać względem serwera).
 */
export const parseStateAt = (stateAt?: string | null, now = Date.now()) => {
  if (!stateAt) return undefined
  const ts = Date.parse(stateAt)
  return Number.isNaN(ts) ? undefined : Math.min(ts, now)
}

/** ms pozostałe do końca aktualnego ruchu */
export const getGateRemainingTime = (gate: GateMotion, now = Date.now()) => {
  if (!isGateMoving(gate.state)) return 0
  const position = getGatePosition(gate, now)
  const distance = Math.abs(getGateTargetPosition(gate, now) - position)
  return distance * (gate.duration ?? DEFAULT_GATE_DURATION)
}

/**
 * Wylicza metadane ruchu dla nowego stanu na podstawie poprzedniego.
 * Jeśli stan się nie zmienił – zachowuje poprzednie metadane.
 */
export const nextGateMotion = (
  prev: GateMotion | undefined,
  state: warehouse.GateState,
  duration: number | undefined,
  now = Date.now(),
  since?: number
): Required<Pick<GateMotion, 'since' | 'startPosition' | 'duration'>> => {
  const sameMotion = prev && prev.state === state && prev.since !== undefined &&
    (since === undefined || since === prev.since)
  if (prev && sameMotion && prev.since !== undefined) {
    return {
      since: prev.since,
      startPosition: prev.startPosition ?? getGatePosition(prev, prev.since),
      duration: prev.duration ?? duration ?? DEFAULT_GATE_DURATION,
    }
  }
  const startPosition = state === 'gate_malfunction'
    ? 0.5
    : prev
    ? getGatePosition(prev, since !== undefined && prev.since !== undefined ? Math.max(since, prev.since) : now)
    : state === 'gate_opening' ? 0 : state === 'gate_closing' ? 1 : getGatePosition({ state })
  return {
    since: since ?? now,
    startPosition,
    duration: duration ?? DEFAULT_GATE_DURATION,
  }
}

/** Czas ruchu bramy (ms) z akcji magazynu przypisanego do danego obiektu */
export const findGateDuration = (
  warehouses: warehouse.Warehouse[],
  placeId: string,
  gateId: string,
  state: warehouse.GateState
) => {
  const actionType = state === 'gate_closing' ? 'close' : 'open'
  for (const w of warehouses) {
    if (w.placeId !== placeId) continue
    const time = w.actions?.gates?.[gateId]?.[actionType]?.time ?? w.actions?.gates?.[gateId]?.open?.time
    if (time) return time * 1000
  }
  return undefined
}
